import { sendToTerminal } from "../../terminalLogger";
import { logPipelineStage, logPipelineError } from "../logging/pipelineLogger";
import { corelCommunication } from "../communication";
import { GerArquivosService } from "../../../services/gerArquivosService";
import { smbCredentialsService } from "../../../services/smbCredentialsService";
import { getActionTimeout } from "../timeoutService";

type PipelineStatus = "success" | "error" | "cancelled";

export interface GerArquivosSaveOptions {
	// Dados necessários para salvar no Corel
	nomeArquivo: string;
	diretorio: string;
	// Dados mínimos para confirmar na API (rota simples /arquivos/save)
	prod_cat_item: 1 | 2 | 3 | 4;
	observacoes?: string;
	cliente_id?: string;
	cli_whatsapp?: string;
	orcamentoId?: number;
	oitId?: number;

	// Configuração
	delaysMs?: { afterSaveCorel?: number };
	timeoutsMs?: { salvararquivo?: number };
	abortSignal?: AbortSignal | null;

	// Alternar modo de confirmação na API:
	// - 'simple': usa GerArquivosService.salvarArquivoCorel (rota /arquivos/save)
	// - 'advanced': chama callback custom para payload full (/cdr/arquivos/salvar)
	apiMode?: "simple" | "advanced";
	confirmApiAdvanced?: (ctx: {
		caminhoSalvo: string;
		fileName: string;
	}) => Promise<boolean>;

	onProgress?: (event: {
		stage: string;
		meta?: Record<string, unknown>;
	}) => void;
}

export interface GerArquivosSaveResult {
	status: PipelineStatus;
	error?: { code: string; message?: string };
	data?: { caminhoSalvo: string; fileName: string };
	metrics?: { saveMs?: number; apiMs?: number; totalMs?: number };
	pipelineId?: string;
}

function sleep(ms: number): Promise<void> {
	return new Promise((r) => setTimeout(r, ms));
}

function ensureCdrExtension(fileName: string): string {
	const trimmed = String(fileName || "").trim();
	if (!trimmed) return "documento.cdr";
	return /\.cdr$/i.test(trimmed) ? trimmed : `${trimmed}.cdr`;
}

function buildFallbackCaminhoSalvo(diretorio: string, nomeArquivo: string): string {
	const separator = /[\\/]$/.test(diretorio) ? "" : "\\";
	return `${diretorio}${separator}${ensureCdrExtension(nomeArquivo)}`;
}

function resolveCaminhoSalvo(result: any, diretorio: string, nomeArquivo: string): string {
	const caminhoRetornado = result?.caminhoSalvo || result?.CaminhoSalvo;
	if (typeof caminhoRetornado === "string" && caminhoRetornado.trim()) {
		return caminhoRetornado.trim();
	}
	return buildFallbackCaminhoSalvo(diretorio, nomeArquivo);
}

export async function runGerArquivosSavePipeline(
	options: GerArquivosSaveOptions,
): Promise<GerArquivosSaveResult> {
	const startedAt = Date.now();
	const pipelineId = `garq-${startedAt}-${Math.random()
		.toString(36)
		.slice(2, 7)}`;

	const emit = (stage: string, meta?: Record<string, unknown>) => {
		try {
			options.onProgress?.({ stage, meta });
		} catch {}
	};

	const delaySaveCorel = options?.delaysMs?.afterSaveCorel ?? 300;
	const timeoutSalvar =
		options?.timeoutsMs?.salvararquivo ?? getActionTimeout("salvararquivo");

	try {
		logPipelineStage(pipelineId, "START", {
			diretorio: options.diretorio,
			nomeArquivo: options.nomeArquivo,
		});
		emit("start", { pipelineId });

		// 1) SALVARARQUIVO (Corel) - salvar fisicamente e obter hash/tamanho/caminho
		const t3 = Date.now();
		emit("save:send", {
			diretorio: options.diretorio,
			nomeArquivo: options.nomeArquivo,
		});

		// Guardar detalhes retornados pelo Corel (hash/tamanho/horário)
		let salvarMeta: {
			hashAlgoritmo?: string;
			hashArquivo?: string;
			tamanhoBytes?: number;
			ultimaGravacaoUtc?: string;
		} = {};

		// ✅ Buscar credenciais SMB se o diretório for UNC (rede)
		const isUncPath =
			options.diretorio &&
			(options.diretorio.startsWith("\\\\") ||
				options.diretorio.startsWith("//"));
		let smbCredential = null;

		if (isUncPath) {
			try {
				smbCredential = await smbCredentialsService.getCredential(3);
			} catch (error) {
				const errorMsg =
					error instanceof Error
						? error.message
						: "Erro desconhecido ao buscar credenciais SMB";
				sendToTerminal(
					"⚠️ [GERARQ:SAVE] Erro ao buscar credenciais SMB - continuando sem SMB",
					{
						pipelineId,
						error: errorMsg,
					},
				);
				// Não falha o pipeline se SMB não for encontrado, apenas continua sem SMB
				smbCredential = null;
			}
		}

		// ✅ Montar payload base
		const salvarPayload: any = {
			nomeArquivo: options.nomeArquivo,
			diretorio: options.diretorio,
			saveMode: "quick" as const,
			timeoutMs: timeoutSalvar,
		};

		// ✅ VALIDAÇÃO E FORMATO CORRETO DO SMB
		// Regra: Se SMB existe E tem username E password → OBRIGATÓRIO incluir no formato correto
		// Se não tiver ambos → NÃO incluir no payload
		if (smbCredential) {
			const username = smbCredential.username
				? String(smbCredential.username).trim()
				: null;
			const passwordValue = smbCredential.password_enc
				? String(smbCredential.password_enc).trim()
				: null;

			// ✅ VALIDAÇÃO: Só adiciona SMB se username E password estiverem presentes e válidos
			if (
				username &&
				username.length > 0 &&
				passwordValue &&
				passwordValue.length > 0
			) {
				const smbPayload: any = {
					username: username,
					password: passwordValue, // ✅ SEMPRE usar "password" (não password_enc)
				};

				// ✅ Domain: Só adiciona se for string não vazia (null/undefined não inclui)
				if (
					typeof smbCredential.domain === "string" &&
					smbCredential.domain.trim().length > 0
				) {
					smbPayload.domain = smbCredential.domain.trim();
				}
				// Se domain for null, undefined ou string vazia → NÃO inclui no payload

				// ✅ OBRIGATÓRIO: Adicionar SMB ao payload quando válido
				salvarPayload.smb = smbPayload;
			} else {
				// ⚠️ SMB existe mas está incompleto → NÃO incluir e logar aviso
				sendToTerminal(
					"⚠️ [GERARQ:SAVE] SMB encontrado mas incompleto - não será incluído",
					{
						pipelineId,
						hasUsername: !!username,
						hasPassword: !!passwordValue,
						reason: !username
							? "username ausente ou vazio"
							: "password ausente ou vazio",
					},
				);
				// NÃO adiciona salvarPayload.smb → payload não terá campo SMB
			}
		}

		const caminhoSalvo = await new Promise<string>((resolve, reject) => {
			const reqId = corelCommunication.sendActionGuarded(
				"salvararquivo",
				salvarPayload as any,
				(resp: any) => {
					const ok = resp?.status === "ok";
					try {
						const result = resp?.result || {};
						salvarMeta = {
							hashAlgoritmo: result.hashAlgoritmo || result.HashAlgoritmo,
							hashArquivo: result.hashArquivo || result.HashArquivo,
							tamanhoBytes: result.tamanhoBytes || result.TamanhoBytes,
							ultimaGravacaoUtc:
								result.ultimaGravacaoUtc || result.UltimaGravacaoUtc,
						};
					} catch {}
					if (ok) {
						resolve(
							resolveCaminhoSalvo(resp?.result || {}, options.diretorio, options.nomeArquivo),
						);
					} else {
						logPipelineError(pipelineId, "SAVE", {
							message: resp?.message || "Falha na action salvararquivo",
						});
						reject(new Error(resp?.message || "Falha na action salvararquivo"));
					}
				},
				timeoutSalvar,
			);
			if (!reqId) reject(new Error("WebView indisponível para salvar"));
		}).catch((e) => {
			const errorMsg = e instanceof Error ? e.message : String(e);
			emit("save:error", { message: errorMsg });
			// Emitir evento de cancelamento para sinalizar que o fluxo deve ser cancelado
			emit("ui:cancel", { message: errorMsg, reason: "save_failed" });
			throw e;
		});

		const saveMs = Date.now() - t3;
		emit("save:ok", { durationMs: saveMs, caminhoSalvo });

		await sleep(delaySaveCorel);

		// 2) UI = EM ESPERA (antes do envio à API)
		emit("ui:waiting");

		// 3) Enviar para API (confirmar salvamento)
		const t4 = Date.now();
		let apiOk = false;

		if (options.apiMode === "advanced") {
			const fileName = caminhoSalvo.split("\\").pop() || options.nomeArquivo;
			if (typeof options.confirmApiAdvanced === "function") {
				apiOk = await options.confirmApiAdvanced({
					caminhoSalvo,
					fileName,
				});
			} else {
				// Fallback padrão avançado: payload full mínimo
				try {
					const res = await GerArquivosService.salvarArquivoCorelAvancado({
						nome_arquivo: fileName,
						caminho_arquivo: caminhoSalvo,
						prod_type: options.prod_cat_item,
						sys_save: true,
						sys_export: false,
						sys_update: "v1",
						objetos: [],
						payload_snapshot: {
							result: { nativos: { author: "GerArquivos" } },
							snapshot: { log: [] },
						},
					});
					apiOk = !!res?.success;
				} catch (err) {
					apiOk = false;
				}
			}
		} else {
			// Modo simples (rota /arquivos/save conforme doc)
			const fileName = caminhoSalvo.split("\\").pop() || options.nomeArquivo;
			const payloadSave: any = {
				prod_cat_item: options.prod_cat_item,
				arquivo_nome: fileName,
				arquivo_caminho: caminhoSalvo,
				arquivo_tamanho:
					typeof salvarMeta?.tamanhoBytes === "number"
						? salvarMeta.tamanhoBytes
						: undefined,
				arquivo_hash:
					salvarMeta?.hashAlgoritmo && salvarMeta?.hashArquivo
						? `${salvarMeta.hashAlgoritmo.toLowerCase()}:${
								salvarMeta.hashArquivo
							}`
						: undefined,
				observacoes: options.observacoes || "",
			};
			if (options.cliente_id)
				payloadSave.cliente_id = Number(options.cliente_id);
			if (options.cli_whatsapp) payloadSave.cli_whatsapp = options.cli_whatsapp;
			if (options.orcamentoId)
				payloadSave.orcamento_id = options.orcamentoId;
			if (options.oitId) payloadSave.oit_id = options.oitId;

			const res = await GerArquivosService.salvarArquivoCorel(payloadSave);
			apiOk = !!res?.success;
		}

		const apiMs = Date.now() - t4;
		if (!apiOk) {
			logPipelineError(pipelineId, "API", {
				code: "api_confirm_failed",
				message: "Falha ao confirmar salvamento na API",
			});
			emit("api:error", { durationMs: apiMs });
			// Emitir evento de cancelamento quando a API falha
			emit("ui:cancel", {
				message: "Falha ao confirmar salvamento na API",
				reason: "api_failed",
			});
			return {
				status: "error",
				error: {
					code: "api_confirm_failed",
					message: "Falha ao confirmar salvamento na API",
				},
				metrics: { saveMs, apiMs, totalMs: Date.now() - startedAt },
				pipelineId,
			};
		}

		emit("api:ok", { durationMs: apiMs });

		// 4) UI = LIBERA E FECHA
		emit("ui:done", { close: true });

		logPipelineStage(pipelineId, "FINISH", {
			saveMs,
			apiMs,
			totalMs: Date.now() - startedAt,
		});
		return {
			status: "success",
			data: {
				caminhoSalvo,
				fileName: caminhoSalvo.split("\\").pop() || options.nomeArquivo,
			},
			metrics: { saveMs, apiMs, totalMs: Date.now() - startedAt },
			pipelineId,
		};
	} catch (e: any) {
		const errorMsg = e?.message || String(e);
		logPipelineError(pipelineId, "PIPELINE", {
			code: "unexpected",
			message: errorMsg || "Erro desconhecido",
		});
		emit("error", { message: errorMsg });
		// Emitir evento de cancelamento quando há erro geral
		emit("ui:cancel", { message: errorMsg, reason: "pipeline_error" });
		return {
			status: "error",
			error: { code: "unexpected", message: errorMsg || "Erro desconhecido" },
			pipelineId,
			metrics: { totalMs: Date.now() - startedAt },
		};
	}
}
